import React from 'react'
// libs
import { change } from 'redux-form'
import { useDispatch } from 'react-redux'
import { useMutation } from '@apollo/client/react'
// hooks
import { useModalData } from 'hooks/index'
// components
import { Modal } from 'components/UI'
import { CloseIcon } from 'components/UI/Icons'
import AiPrompt from 'components/Modals/AiFieldValueModal/components/AiPrompt'
import AiResultActions from 'components/Modals/AiFieldValueModal/components/AiResultActions'
// context
import { RiskAPIContext } from 'containers/Risks/Risk/Edit/layers/API'
// views
import { Content, Disclaimer, Explanation, Header, Section, SectionLabel, SectionValue } from './views'
// constants
import { Forms } from 'constants/forms'
import { IField } from 'types/graphql'
import { Modals } from 'constants/modals'
// graphql
import { GENERATE_AI_RISK_ASSESSMENT_MUTATION } from 'graphql/risk/mutations'
// utils
import { handleError } from 'utils/errors/handlers'

type ModalData = {
  field?: IField
  riskId?: string
}

type Result = {
  displayValue: { label: string; color: string }
  value: string
  explanation: string
}

type Step = 'prompt' | 'result'

const AiMatrixValueModal: React.FC = () => {
  const dispatch = useDispatch()

  const [step, setStep] = React.useState<Step>('prompt')
  const [value, setValue] = React.useState<Result | null>(null)
  const [loading, setLoading] = React.useState(false)

  const abortControllerRef = React.useRef<AbortController | null>(null)

  const { risk } = React.useContext(RiskAPIContext)

  const [generateAiRiskAssessment] = useMutation<any>(GENERATE_AI_RISK_ASSESSMENT_MUTATION)

  const {
    hideModal,
    modalData: { isOpen, field }
  } = useModalData<ModalData>({ modalType: Modals.AI_MATRIX_VALUE_MODAL })

  React.useEffect(() => {
    if (!isOpen) {
      abortControllerRef.current?.abort()
      setStep('prompt')
      setValue(null)
    }
  }, [isOpen])

  const generateValue = async (prompt: string) => {
    const controller = new AbortController()
    abortControllerRef.current = controller

    setLoading(true)

    try {
      const {
        data: { generateAiRiskAssessment: data }
      } = await generateAiRiskAssessment({
        variables: { input: { fieldId: field.id, riskId: risk.id, userMessage: prompt } },
        context: { fetchOptions: { signal: controller.signal } }
      })

      const selectedOptionId = field.settings.bRightSection.flat().find((item) => item.id === data?.value.value)?.value
      const selectedOption = field.settings.options.find((option) => option.id === selectedOptionId)

      setValue({ displayValue: selectedOption, value: data?.value, explanation: data?.explanation })
      setStep('result')
    } catch (e) {
      if (!controller.signal.aborted) {
        handleError(e, 'AiMatrixResultModal.generateValue')
      }
    } finally {
      setLoading(false)
      abortControllerRef.current = null
    }
  }

  const onApplyClick = () => {
    if (value) {
      dispatch(change(Forms.RISK_TEMPLATE_FORM, `${field.name}.value`, value.value))
    }
    hideModal()
  }

  const onTryAgain = () => {
    setStep('prompt')
    setValue(null)
  }

  const onCancel = () => {
    abortControllerRef.current?.abort()
    hideModal()
  }

  if (!isOpen) {
    return null
  }

  return (
    <Modal hideModal={onCancel} isOpen={isOpen} width='sm'>
      <Content>
        <Header>
          {`Generate a value for ${field.label}`}
          <CloseIcon onClick={onCancel} />
        </Header>
        {step === 'prompt' && !loading && (
          <AiPrompt
            onCancel={onCancel}
            onConfirm={generateValue}
            suggestedPrompts={field?.aiSuggestedPrompts}
            description='Describe what context you want rAI to consider for this risk assessment (optional)'
            placeholder='e.g. This is a financial risk in a regulated environment with existing controls'
          />
        )}
        {step === 'result' && (
          <>
            {value?.displayValue && (
              <Section>
                <SectionLabel>Generated Value</SectionLabel>
                <SectionValue $color={value.displayValue.color}>{value.displayValue.label}</SectionValue>
              </Section>
            )}
            {value?.explanation && (
              <Section>
                <SectionLabel>Explanation</SectionLabel>
                <Explanation>{value.explanation}</Explanation>
              </Section>
            )}
            <Disclaimer>rAI can make mistakes. Check important info.</Disclaimer>
          </>
        )}
        {(loading || step === 'result') && (
          <AiResultActions
            loading={loading && step === 'prompt'}
            onCancel={onCancel}
            onApply={onApplyClick}
            onTryAgain={onTryAgain}
            applyDisabled={loading || !value}
          />
        )}
      </Content>
    </Modal>
  )
}

export default AiMatrixValueModal
